import React, { useEffect, useMemo, useState } from 'react';
import {
  SECTION_REPEAT_MAX,
  SECTION_REPEAT_MIN,
  formatSectionRepeat,
} from '@chordbaker/music-theory';
import { useTheme, Theme } from '../contexts/ThemeContext';
import SectionEditorModalFrame from './SectionEditorModalFrame';
import SectionRepeatPill from './SectionRepeatPill';

const REPEAT_OPTIONS = Array.from(
  { length: SECTION_REPEAT_MAX - SECTION_REPEAT_MIN + 1 },
  (_, i) => SECTION_REPEAT_MIN + i,
);

const createStyles = (theme: Theme) => ({
  previewContainer: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing.xl,
    paddingBottom: theme.spacing.xl,
  },
  previewBox: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    maxWidth: '100%',
    backgroundColor: theme.isDark ? theme.colors.surfaceSecondary : theme.colors.surface,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.radii.lg,
    paddingLeft: theme.spacing.xl,
    paddingRight: theme.spacing.xl,
    paddingTop: theme.spacing.md,
    paddingBottom: theme.spacing.md,
  },
  previewTitle: {
    fontSize: theme.typography.sizes.xxxl,
    fontWeight: theme.typography.weights.light,
    color: theme.colors.text,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  sectionContainer: {
    paddingLeft: theme.spacing.xl,
    paddingRight: theme.spacing.xl,
    paddingBottom: theme.spacing.xl,
  },
  sectionLabel: {
    fontSize: theme.typography.sizes.md,
    fontWeight: theme.typography.weights.medium,
    color: theme.colors.text,
    margin: 0,
  },
  optionGrid: {
    display: 'flex',
    flexDirection: 'row' as const,
    flexWrap: 'wrap' as const,
    gap: theme.spacing.sm,
    marginTop: theme.spacing.sm,
  },
  optionButton: {
    backgroundColor: theme.colors.surfaceSecondary,
    border: 'none',
    borderRadius: theme.radii.md,
    paddingLeft: theme.spacing.sm,
    paddingRight: theme.spacing.sm,
    minWidth: 44,
    height: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.text,
    fontVariantNumeric: 'tabular-nums',
    cursor: 'pointer',
    transition: `all ${theme.motion.duration.fast}ms ${theme.motion.easing.standard}`,
  },
  optionButtonSelected: {
    backgroundColor: theme.colors.primary,
    color: theme.colors.textInverse,
  },
});

export interface SectionRepeatEditorModalProps {
  isOpen: boolean;
  sectionTitle: string;
  initialValue: number | null;
  onClose: () => void;
  /** null clears the repeat. */
  onApply: (repeat: number | null) => void;
}

const SectionRepeatEditorModal: React.FC<SectionRepeatEditorModalProps> = ({
  isOpen,
  sectionTitle,
  initialValue,
  onClose,
  onApply,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const [selected, setSelected] = useState<number>(initialValue ?? SECTION_REPEAT_MIN);

  // Start from the section's current value each time the picker opens
  useEffect(() => {
    if (isOpen) {
      setSelected(initialValue ?? SECTION_REPEAT_MIN);
    }
  }, [isOpen, initialValue]);

  return (
    <SectionEditorModalFrame
      isOpen={isOpen}
      title="Repeat section"
      onClose={onClose}
      onApply={() => onApply(selected)}
      onClear={initialValue != null ? () => onApply(null) : undefined}
    >
      <div style={styles.previewContainer}>
        <div style={styles.previewBox}>
          {sectionTitle.trim() && <span style={styles.previewTitle}>{sectionTitle}</span>}
          <SectionRepeatPill repeat={selected} />
        </div>
      </div>
      <div style={styles.sectionContainer}>
        <p style={styles.sectionLabel}>Times played</p>
        <div style={styles.optionGrid} role="radiogroup" aria-label="Times played">
          {REPEAT_OPTIONS.map(value => {
            const isSelected = value === selected;
            return (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={isSelected}
                onClick={() => setSelected(value)}
                style={{
                  ...styles.optionButton,
                  ...(isSelected ? styles.optionButtonSelected : {}),
                }}
              >
                {formatSectionRepeat(value)}
              </button>
            );
          })}
        </div>
      </div>
    </SectionEditorModalFrame>
  );
};

export default SectionRepeatEditorModal;
